/**
 * Documents Service
 * Provides helpers used by the RAG pipeline and validation workflow.
 * 
 * NOTE: The primary document upload path is documentStorage.saveDocument()
 * which handles Storage upload + metadata insert in one operation.
 * uploadDocument() here is a lightweight metadata-only fallback.
 */

export const documentsService = {
    getDocuments: async ({ folder = 'all', role = 'member' } = {}) => {
        // Sprint 4: query supabase documents table with RLS
        return [];
    },

    uploadDocument: async (file, metadata = {}) => {
        const { supabase } = await import('../lib/supabase');
        const category = metadata.category;
        const hoaId = metadata.hoaId;
        if (!hoaId) throw new Error('documentsService.uploadDocument: metadata.hoaId is required');

        const { data, error } = await supabase
            .from('documents')
            .insert({
                hoa_id: hoaId,
                title: metadata.name || file.name,
                file_name: file.name,
                file_url: metadata.file_url || 'pending',
                category: category,          // must be a valid enum value
                validation_status: 'pending',
                validation_level: 0,
                is_indexed: false,
            })
            .select()
            .single();

        if (error) {
            console.error('Insert error:', error);
            throw error;
        }

        return { success: true, ...data };
    },

    deleteDocument: async (id) => {
        const { default: documentStorage } = await import('./documentStorage');
        await documentStorage.deleteDocument(id);
        return { success: true };
    },

    getSignedUrl: async (path) => {
        if (!path || path === 'pending') return { url: null };
        const { supabase } = await import('../lib/supabase');
        const { data } = await supabase.storage
            .from('documents')
            .createSignedUrl(path, 3600);
        return { url: data?.signedUrl || null };
    },

    updateDocumentExtraction: async (id, extractionData) => {
        const { default: documentStorage } = await import('./documentStorage');
        await documentStorage.updateDocumentMetadata(id, extractionData);
        return { success: true };
    },

    updateDocumentStatus: async (id, status) => {
        const { default: documentStorage } = await import('./documentStorage');
        await documentStorage.updateDocumentMetadata(id, { ai_status: status });
        return { success: true };
    },

    // Checks whether chunking + embedding have actually finished for a document.
    // runIngestionPipeline runs fire-and-forget from saveDocument, decoupled from
    // the upload modal's Level 1-3 flow — there is no other signal that ingestion
    // is done. Level 4 must not run its stress test against a partially-indexed
    // or unembedded document, or a real ingestion failure would look like a bad
    // answer instead of what it actually is.
    getIngestionReadiness: async (id) => {
        const { supabase } = await import('../lib/supabase');

        const { data: doc, error: docError } = await supabase
            .from('documents')
            .select('is_indexed, chunk_count')
            .eq('id', id)
            .single();

        if (docError) {
            return { ready: false, error: docError.message, chunkCount: 0, embeddedCount: 0 };
        }
        if (!doc?.is_indexed || !doc?.chunk_count) {
            return { ready: false, error: null, chunkCount: doc?.chunk_count || 0, embeddedCount: 0 };
        }

        const { count: activeCount, error: activeError } = await supabase
            .from('document_chunks')
            .select('id', { count: 'exact', head: true })
            .eq('document_id', id)
            .is('invalidated_at', null);

        const { count: embeddedCount, error: embeddedError } = await supabase
            .from('document_chunks')
            .select('id', { count: 'exact', head: true })
            .eq('document_id', id)
            .is('invalidated_at', null)
            .not('embedding', 'is', null);

        if (activeError || embeddedError) {
            return {
                ready: false,
                error: (activeError || embeddedError).message,
                chunkCount: doc.chunk_count,
                embeddedCount: 0,
            };
        }

        const ready = activeCount > 0 && embeddedCount === activeCount;
        return { ready, error: null, chunkCount: activeCount, embeddedCount };
    },

    // Read-only health check for vector embeddings across an HOA's ACTIVE chunks.
    // runIngestionPipeline swallows embed-chunks failures (console.warn only), so a
    // bad/missing VOYAGE_API_KEY leaves chunks with embedding NULL — search_chunks
    // then silently falls back to full-text only for those documents. This surfaces
    // that gap to the admin. Invalidated chunks are excluded: they are superseded
    // re-index leftovers, never searched, and would otherwise be false alarms.
    // Chunks younger than EMBEDDING_GRACE_MS are reported as "processing", not
    // missing, since embed-chunks runs right after insert.
    getEmbeddingHealth: async (hoaId) => {
        const EMBEDDING_GRACE_MS = 10 * 60 * 1000;
        const MAX_ROWS = 1000;
        const { supabase } = await import('../lib/supabase');

        if (!hoaId) {
            return { error: 'No HOA selected', activeChunks: 0, missingChunks: 0, documents: [], truncated: false };
        }

        try {
            const { count: activeChunks, error: activeError } = await supabase
                .from('document_chunks')
                .select('id', { count: 'exact', head: true })
                .eq('hoa_id', hoaId)
                .is('invalidated_at', null);
            if (activeError) throw activeError;

            const { data: missingRows, error: missingError } = await supabase
                .from('document_chunks')
                .select('document_id, created_at')
                .eq('hoa_id', hoaId)
                .is('invalidated_at', null)
                .is('embedding', null)
                .range(0, MAX_ROWS - 1);
            if (missingError) throw missingError;

            const rows = missingRows || [];
            const byDoc = new Map();
            for (const row of rows) {
                const entry = byDoc.get(row.document_id) || { documentId: row.document_id, missing: 0, newest: null };
                entry.missing += 1;
                if (!entry.newest || row.created_at > entry.newest) entry.newest = row.created_at;
                byDoc.set(row.document_id, entry);
            }

            let docMeta = [];
            if (byDoc.size > 0) {
                const { data, error: docError } = await supabase
                    .from('documents')
                    .select('id, title, ai_status')
                    .eq('hoa_id', hoaId)
                    .in('id', [...byDoc.keys()]);
                if (docError) throw docError;
                docMeta = data || [];
            }
            const metaById = new Map(docMeta.map(d => [d.id, d]));

            const now = Date.now();
            const documents = [...byDoc.values()]
                .map(entry => {
                    const meta = metaById.get(entry.documentId);
                    return {
                        ...entry,
                        title: meta?.title || 'Untitled document',
                        aiStatus: meta?.ai_status || 'unknown',
                        processing: entry.newest ? now - new Date(entry.newest).getTime() < EMBEDDING_GRACE_MS : false,
                    };
                })
                // Live documents first — those are the ones members are searching right now
                .sort((a, b) => (a.aiStatus === 'live' ? 0 : 1) - (b.aiStatus === 'live' ? 0 : 1) || b.missing - a.missing);

            const missingChunks = documents.filter(d => !d.processing).reduce((sum, d) => sum + d.missing, 0);

            return {
                error: null,
                activeChunks: activeChunks || 0,
                missingChunks,
                documents,
                truncated: rows.length >= MAX_ROWS,
            };
        } catch (err) {
            return { error: err?.message || 'Embedding health check failed', activeChunks: 0, missingChunks: 0, documents: [], truncated: false };
        }
    },
};
